import { BaseSqliteRepository } from './base-sqlite.repository';
import type {
  ExternalTransactionIdentityLink,
  ExternalTransactionLink,
  ExternalTransactionUpsert,
  IExternalTransactionRepository,
} from '@app/domain/ports';

export class ExternalTransactionRepository extends BaseSqliteRepository implements IExternalTransactionRepository {
  async findLinkByExternalId(source: string, externalId: string): Promise<ExternalTransactionLink | null> {
    const row = await this.queryOne<ExternalTransactionLink>(
      `SELECT expense_id as expenseId,
              income_id as incomeId,
              account_transfer_id as transferId,
              exchange_id as exchangeId,
              skip_similar_match as skipSimilarMatch
       FROM external_transactions
       WHERE source = ? AND external_id = ? AND is_deleted = 0
       LIMIT 1`,
      [source, externalId],
    );
    if (!row) return null;
    return {
      expenseId: row.expenseId,
      incomeId: row.incomeId,
      transferId: row.transferId,
      exchangeId: row.exchangeId,
      ...(row.skipSimilarMatch ? { skipSimilarMatch: true } : {}),
    };
  }

  async findLinksByExternalIds(source: string, externalIds: string[]): Promise<Map<string, ExternalTransactionLink>> {
    if (externalIds.length === 0) {
      return new Map();
    }

    const uniqueExternalIds = [...new Set(externalIds)];
    const placeholders = uniqueExternalIds.map(() => '?').join(', ');
    const rows = await this.query<
      ExternalTransactionLink & {
        externalId: string;
      }
    >(
      `SELECT external_id as externalId,
              expense_id as expenseId,
              income_id as incomeId,
              account_transfer_id as transferId,
              exchange_id as exchangeId,
              skip_similar_match as skipSimilarMatch
       FROM external_transactions
       WHERE source = ?
         AND external_id IN (${placeholders})
         AND is_deleted = 0`,
      [source, ...uniqueExternalIds],
    );

    const map = new Map<string, ExternalTransactionLink>();
    for (const row of rows) {
      map.set(row.externalId, {
        expenseId: row.expenseId,
        incomeId: row.incomeId,
        transferId: row.transferId,
        exchangeId: row.exchangeId,
        ...(row.skipSimilarMatch ? { skipSimilarMatch: true } : {}),
      });
    }

    return map;
  }

  async upsertMany(links: ExternalTransactionUpsert[]): Promise<void> {
    if (links.length === 0) {
      return;
    }

    const placeholders = links.map(() => '(?, ?, ?, ?, ?, ?, ?, strftime(\'%s\',\'now\'), 0)').join(', ');
    const params = links.flatMap((link) => [
      link.externalId,
      link.externalAccountId,
      link.expenseId,
      link.incomeId,
      link.transferId,
      link.exchangeId,
      link.source,
    ]);

    await this.execute(
      `INSERT INTO external_transactions (
         external_id,
         external_account_id,
         expense_id,
         income_id,
         account_transfer_id,
         exchange_id,
         source,
         imported_at,
         is_deleted
       )
       VALUES ${placeholders}
       ON CONFLICT(source, external_id, is_deleted) DO UPDATE SET
         external_account_id = excluded.external_account_id,
         expense_id = excluded.expense_id,
         income_id = excluded.income_id,
         account_transfer_id = excluded.account_transfer_id,
         exchange_id = excluded.exchange_id,
         skip_similar_match = 0,
         imported_at = excluded.imported_at`,
      params,
    );
  }

  /**
   * Find expense ID by external transaction ID
   * @param source Source system (e.g., 'monobank')
   * @param externalId External transaction ID
   * @returns Expense ID if found, null otherwise
   */
  async findExpenseIdByExternalId(source: string, externalId: string): Promise<number | null> {
    const result = await this.queryOne<{ expenseId: number }>(
      `SELECT expense_id as expenseId
       FROM external_transactions
       WHERE source = ? AND external_id = ? AND is_deleted = 0 AND expense_id IS NOT NULL
       LIMIT 1`,
      [source, externalId],
    );

    return result?.expenseId ?? null;
  }

  /**
   * Find income ID by external transaction ID
   * @param source Source system (e.g., 'monobank')
   * @param externalId External transaction ID
   * @returns Income ID if found, null otherwise
   */
  async findIncomeIdByExternalId(source: string, externalId: string): Promise<number | null> {
    const result = await this.queryOne<{ incomeId: number }>(
      `SELECT income_id as incomeId
       FROM external_transactions
       WHERE source = ? AND external_id = ? AND is_deleted = 0 AND income_id IS NOT NULL
       LIMIT 1`,
      [source, externalId],
    );

    return result?.incomeId ?? null;
  }

  /**
   * Find transfer ID by external transaction ID
   * @param source Source system (e.g., 'monobank')
   * @param externalId External transaction ID
   * @returns Transfer ID if found, null otherwise
   */
  async findTransferIdByExternalId(source: string, externalId: string): Promise<number | null> {
    const result = await this.queryOne<{ transferId: number }>(
      `SELECT account_transfer_id as transferId
       FROM external_transactions
       WHERE source = ? AND external_id = ? AND is_deleted = 0 AND account_transfer_id IS NOT NULL
       LIMIT 1`,
      [source, externalId],
    );

    return result?.transferId ?? null;
  }

  /**
   * Find exchange ID by external transaction ID
   * @param source Source system (e.g., 'monobank')
   * @param externalId External transaction ID
   * @returns Exchange ID if found, null otherwise
   */
  async findExchangeIdByExternalId(source: string, externalId: string): Promise<number | null> {
    const result = await this.queryOne<{ exchangeId: number }>(
      `SELECT exchange_id as exchangeId
       FROM external_transactions
       WHERE source = ? AND external_id = ? AND is_deleted = 0 AND exchange_id IS NOT NULL
       LIMIT 1`,
      [source, externalId],
    );

    return result?.exchangeId ?? null;
  }

  /**
   * Insert a new external transaction record
   */
  async insert(
    source: string,
    externalId: string,
    externalAccountId: string | null,
    expenseId: number | null,
    incomeId: number | null,
    transferId: number | null,
    exchangeId: number | null = null,
  ): Promise<void> {
    await this.execute(
      `INSERT INTO external_transactions (source, external_id, external_account_id, expense_id, income_id, account_transfer_id, exchange_id, imported_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, strftime('%s','now'))`,
      [source, externalId, externalAccountId, expenseId, incomeId, transferId, exchangeId],
    );
  }

  async findLinksByExpenseId(expenseId: number): Promise<ExternalTransactionIdentityLink[]> {
    return this.findLinksByFinancialId('expense_id', expenseId);
  }

  async findLinksByIncomeId(incomeId: number): Promise<ExternalTransactionIdentityLink[]> {
    return this.findLinksByFinancialId('income_id', incomeId);
  }

  async findLinksByTransferId(transferId: number): Promise<ExternalTransactionIdentityLink[]> {
    return this.findLinksByFinancialId('account_transfer_id', transferId);
  }

  async detachLinksByExpenseId(expenseId: number): Promise<void> {
    await this.execute(
      `UPDATE external_transactions
       SET expense_id = NULL, skip_similar_match = 1
       WHERE expense_id = ? AND is_deleted = 0`,
      [expenseId],
    );
  }

  async detachLinksByIncomeId(incomeId: number): Promise<void> {
    await this.execute(
      `UPDATE external_transactions
       SET income_id = NULL, skip_similar_match = 1
       WHERE income_id = ? AND is_deleted = 0`,
      [incomeId],
    );
  }

  async setTransferIdByExpenseId(expenseId: number, transferId: number): Promise<void> {
    await this.execute(
      `UPDATE external_transactions
       SET account_transfer_id = ?
       WHERE expense_id = ? AND is_deleted = 0`,
      [transferId, expenseId],
    );
  }

  async setTransferIdByIncomeId(incomeId: number, transferId: number): Promise<void> {
    await this.execute(
      `UPDATE external_transactions
       SET account_transfer_id = ?
       WHERE income_id = ? AND is_deleted = 0`,
      [transferId, incomeId],
    );
  }

  async reapplyExpenseId(source: string, externalId: string, expenseId: number): Promise<void> {
    await this.execute(
      `UPDATE external_transactions
       SET expense_id = ?
       WHERE source = ? AND external_id = ? AND is_deleted = 0`,
      [expenseId, source, externalId],
    );
  }

  async reapplyIncomeId(source: string, externalId: string, incomeId: number): Promise<void> {
    await this.execute(
      `UPDATE external_transactions
       SET income_id = ?
       WHERE source = ? AND external_id = ? AND is_deleted = 0`,
      [incomeId, source, externalId],
    );
  }

  async updateLinkByExternalId(
    source: string,
    externalId: string,
    externalAccountId: string | null,
    expenseId: number | null,
    incomeId: number | null,
    transferId: number | null,
    exchangeId: number | null = null,
  ): Promise<void> {
    await this.execute(
      `UPDATE external_transactions
       SET external_account_id = ?,
           expense_id = ?,
           income_id = ?,
           account_transfer_id = ?,
           exchange_id = ?,
           skip_similar_match = 0,
           imported_at = strftime('%s','now')
       WHERE source = ? AND external_id = ? AND is_deleted = 0`,
      [externalAccountId, expenseId, incomeId, transferId, exchangeId, source, externalId],
    );
  }

  private async findLinksByFinancialId(
    column: 'expense_id' | 'income_id' | 'account_transfer_id',
    id: number,
  ): Promise<ExternalTransactionIdentityLink[]> {
    return this.query<ExternalTransactionIdentityLink>(
      `SELECT source as source,
              external_id as externalId,
              expense_id as expenseId,
              income_id as incomeId,
              account_transfer_id as transferId,
              exchange_id as exchangeId
       FROM external_transactions
       WHERE ${column} = ? AND is_deleted = 0`,
      [id],
    );
  }
}
